import { lerpColor } from '../../color';
import { calmAt, calmCenter, glowDot, grain, rgba, vignette, type Painter, type Spot } from '../../art/paint';
import type { Rng } from '../../../core/rng';
import { hsl, type World } from '../../world';

const TAU = Math.PI * 2;
const FAR = hsl(125, 26, 16);
const PUMP = hsl(28, 50, 28);
const PUMP_LIGHT = hsl(30, 56, 36);
const PUMP_DARK = hsl(24, 45, 15);
const STEM = hsl(85, 28, 24);
const VINE = hsl(112, 30, 25);
const LEAF = hsl(125, 32, 19);
const WOOD = hsl(30, 20, 20);
const SACK = hsl(38, 28, 32);
const HAT = hsl(260, 14, 15);
const SHIRT = hsl(275, 22, 26);
const PATCH = hsl(28, 35, 30);
const STRAW = hsl(42, 35, 36);
const OWL = hsl(30, 18, 22);
const BAT = [0, -0.15, 0.3, -0.42, 0.72, -0.48, 1, -0.22, 0.8, -0.08, 0.62, 0.06, 0.44, -0.04, 0.24, 0.12, 0, 0.24];

type Line = (x: number) => number;

interface Layout {
  readonly portrait: boolean;
  readonly horizon: number;
  readonly scale: number;
  readonly ground: Line;
}

interface Moon {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

interface Gourd {
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly carved: boolean;
}

interface Post {
  readonly x: number;
  readonly y: number;
  readonly top: Spot;
  readonly open: boolean;
}

function layoutOf(p: Painter): Layout {
  const portrait = p.w / p.h < 0.8;
  const low = p.h * (portrait ? 0.87 : 0.95);
  const rise = p.h * (portrait ? 0.07 : 0.22);
  return {
    portrait,
    horizon: p.h * (portrait ? 0.76 : 0.62),
    scale: portrait ? 0.06 * Math.max(p.unit, 0.36 * p.h) : p.unit * 0.11,
    ground: (x) => low - rise * ((x - p.cx) / (p.w / 2)) ** 2 + Math.sin((x / p.w) * 9.5) * p.h * 0.006,
  };
}

function poly(ctx: CanvasRenderingContext2D, pts: readonly number[], x: number, y: number, sx: number, sy: number): void {
  ctx.beginPath();
  for (let i = 0; i < pts.length; i += 2) {
    const px = x + (pts[i] ?? 0) * sx;
    const py = y + (pts[i + 1] ?? 0) * sy;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

function sky(p: Painter, look: World, lay: Layout): void {
  const { ctx } = p;
  const { bg, ink } = look.palette;
  const g = ctx.createLinearGradient(0, 0, 0, lay.horizon);
  g.addColorStop(0, rgba(lerpColor(bg, ink, 0.55)));
  g.addColorStop(0.55, rgba(bg));
  g.addColorStop(1, rgba(lerpColor(bg, FAR, 0.6)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
}

function stars(p: Painter, look: World, lay: Layout): void {
  const { ctx } = p;
  const rng = p.rng.fork('stars');
  const count = Math.round(Math.min(60, Math.max(22, (p.w * p.h) / 16000)));
  const dot = Math.max(0.6, p.unit * 0.0035);
  for (let i = 0; i < count; i++) {
    const x = rng.range(6, p.w - 6);
    const y = rng.range(4, lay.horizon - p.h * 0.06);
    const size = dot * rng.range(0.6, 1.6);
    const alpha = rng.range(0.16, 0.48);
    if (calmAt(p, x, y, 0.96)) continue;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, TAU);
    ctx.fillStyle = rgba(look.palette.rim, alpha);
    ctx.fill();
  }
}

function moonOf(p: Painter, lay: Layout): Moon {
  if (!lay.portrait) return { x: p.w * 0.84, y: p.h * 0.3, r: p.unit * 0.15 };
  const r = Math.max(24, Math.min(p.w, p.h) * 0.085);
  return { x: Math.max(r * 1.6, p.w * 0.16), y: p.h * 0.27, r };
}

function moon(p: Painter, look: World, m: Moon): void {
  const { ctx } = p;
  const { rim } = look.palette;
  glowDot(ctx, m.x, m.y, m.r * 3.2, rim, 0.1);
  const g = ctx.createRadialGradient(m.x - m.r * 0.35, m.y - m.r * 0.35, m.r * 0.1, m.x, m.y, m.r);
  g.addColorStop(0, rgba(lerpColor(look.ceil, rim, 0.24)));
  g.addColorStop(1, rgba(lerpColor(look.ceil, rim, 0.08)));
  ctx.beginPath();
  ctx.arc(m.x, m.y, m.r, 0, TAU);
  ctx.fillStyle = g;
  ctx.fill();
  for (const [dx, dy, rr] of [
    [-0.3, 0.15, 0.22],
    [0.28, -0.25, 0.16],
    [0.24, 0.34, 0.12],
    [-0.08, -0.44, 0.09],
  ] as const) {
    ctx.beginPath();
    ctx.arc(m.x + dx * m.r, m.y + dy * m.r, rr * m.r, 0, TAU);
    ctx.fillStyle = rgba(lerpColor(look.ceil, FAR, 0.3), 0.4);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(m.x, m.y, m.r, 0, TAU);
  ctx.strokeStyle = rgba(rim, 0.32);
  ctx.lineWidth = Math.max(1, m.r * 0.03);
  ctx.stroke();
}

function bats(p: Painter, look: World, m: Moon): void {
  const { ctx } = p;
  const flock = p.rng.fork('bats');
  const side = m.x > p.cx ? -1 : 1;
  for (let i = 0; i < 5; i++) {
    const s = m.r * flock.range(0.24, 0.4);
    const x = m.x + side * m.r * flock.range(-0.4, 2.4);
    const y = Math.max(p.h * 0.2 + s, m.y + m.r * flock.range(-1, 1.4));
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(flock.range(-0.3, 0.3));
    for (const dir of [1, -1]) {
      poly(ctx, BAT, 0, 0, dir * s, s);
      ctx.fillStyle = rgba(lerpColor(look.palette.ink, look.palette.spot, 0.1), 0.88);
      ctx.fill();
      ctx.strokeStyle = rgba(lerpColor(FAR, look.palette.spot, 0.3), 0.35);
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    ctx.restore();
  }
}

function ridge(p: Painter, base: number, amp: number, waves: number, rng: Rng): Line {
  const phase = rng.range(0, TAU);
  const twist = rng.range(0, TAU);
  return (x) => {
    const t = x / p.w;
    return base + Math.sin(phase + t * waves * TAU) * amp + Math.sin(twist + t * waves * 2.6 * TAU) * amp * 0.35;
  };
}

function fillRidge(p: Painter, line: Line, color: number, edge: number): void {
  const { ctx } = p;
  const step = Math.max(6, p.w / 90);
  ctx.beginPath();
  ctx.moveTo(0, p.h);
  for (let x = 0; x <= p.w + step; x += step) ctx.lineTo(x, line(x));
  ctx.lineTo(p.w, p.h);
  ctx.closePath();
  ctx.fillStyle = rgba(color);
  ctx.fill();
  ctx.beginPath();
  for (let x = 0; x <= p.w + step; x += step) {
    if (x === 0) ctx.moveTo(x, line(x));
    else ctx.lineTo(x, line(x));
  }
  ctx.strokeStyle = rgba(edge, 0.3);
  ctx.lineWidth = 1;
  ctx.stroke();
}

function hills(p: Painter, look: World, lay: Layout): Line {
  const rng = p.rng.fork('hills');
  const edge = lerpColor(FAR, look.palette.path, 0.22);
  const back = ridge(p, lay.horizon - p.h * 0.05, p.h * 0.03, 1.2, rng);
  fillRidge(p, back, lerpColor(look.palette.bg, FAR, 0.7), edge);
  const front = ridge(p, lay.horizon, p.h * 0.02, 1.9, rng);
  fillRidge(p, front, FAR, edge);
  return front;
}

function fence(p: Painter, look: World, line: Line, lay: Layout): Post[] {
  const { ctx } = p;
  const rng = p.rng.fork('fence');
  const tall = lay.scale * 1.2;
  const posts: Post[] = [];
  for (let x = rng.range(0, tall); x < p.w + tall; x += tall * rng.range(0.9, 1.6)) {
    const tilt = rng.range(-0.2, 0.2);
    const h = tall * rng.range(0.8, 1.15);
    const y = line(x) + tall * 0.12;
    const top = { x: x + Math.sin(tilt) * h, y: y - Math.cos(tilt) * h };
    posts.push({ x, y, top, open: !calmAt(p, x, y - h * 0.5, 0.95) });
  }
  const color = rgba(lerpColor(FAR, look.palette.path, 0.2), 0.5);
  ctx.lineCap = 'round';
  ctx.strokeStyle = color;
  for (let i = 0; i + 1 < posts.length; i++) {
    const a = posts[i];
    const b = posts[i + 1];
    if (a === undefined || b === undefined || !a.open || !b.open) continue;
    for (const f of [0.38, 0.78]) {
      const fa = f + rng.range(-0.08, 0.08);
      const fb = f + rng.range(-0.08, 0.08);
      ctx.beginPath();
      ctx.moveTo(a.x + (a.top.x - a.x) * fa, a.y + (a.top.y - a.y) * fa);
      ctx.lineTo(b.x + (b.top.x - b.x) * fb, b.y + (b.top.y - b.y) * fb);
      ctx.lineWidth = Math.max(1, tall * 0.05);
      ctx.stroke();
    }
  }
  for (const post of posts) {
    if (!post.open) continue;
    ctx.beginPath();
    ctx.moveTo(post.x, post.y);
    ctx.lineTo(post.top.x, post.top.y);
    ctx.lineWidth = Math.max(1.5, tall * 0.08);
    ctx.stroke();
  }
  return posts.filter((post) => post.open);
}

function owl(p: Painter, look: World, posts: readonly Post[], lay: Layout): void {
  const want = lay.portrait ? p.w * 0.78 : p.w * 0.9;
  let at: Post | undefined;
  for (const post of posts) if (at === undefined || Math.abs(post.x - want) < Math.abs(at.x - want)) at = post;
  if (at === undefined) return;
  const { ctx } = p;
  const { ink, held } = look.palette;
  const s = lay.scale * 0.7;
  const x = at.top.x;
  const y = at.top.y;
  ctx.beginPath();
  ctx.moveTo(x - s * 0.3, y - s * 0.78);
  ctx.lineTo(x - s * 0.3, y - s * 1.08);
  ctx.lineTo(x - s * 0.12, y - s * 0.9);
  ctx.lineTo(x + s * 0.12, y - s * 0.9);
  ctx.lineTo(x + s * 0.3, y - s * 1.08);
  ctx.lineTo(x + s * 0.3, y - s * 0.78);
  ctx.closePath();
  ctx.fillStyle = rgba(OWL);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(x, y - s * 0.5, s * 0.34, s * 0.5, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(ink, 0.5);
  ctx.lineWidth = Math.max(1, s * 0.04);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(x, y - s * 0.32, s * 0.2, s * 0.26, 0, 0, TAU);
  ctx.fillStyle = rgba(lerpColor(OWL, STRAW, 0.35));
  ctx.fill();
  glowDot(ctx, x, y - s * 0.72, s * 0.5, held, 0.1);
  for (const dir of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(x + dir * s * 0.14, y - s * 0.72, s * 0.11, 0, TAU);
    ctx.fillStyle = rgba(held, 0.55);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x + dir * s * 0.14, y - s * 0.72, s * 0.045, 0, TAU);
    ctx.fillStyle = rgba(ink, 0.9);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.moveTo(x - s * 0.04, y - s * 0.62);
  ctx.lineTo(x + s * 0.04, y - s * 0.62);
  ctx.lineTo(x, y - s * 0.54);
  ctx.closePath();
  ctx.fillStyle = rgba(STRAW, 0.8);
  ctx.fill();
}

function fogBank(p: Painter, look: World, x: number, y: number, rx: number, ry: number, alpha: number): void {
  const { ctx } = p;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, ry / rx);
  glowDot(ctx, 0, 0, rx, look.palette.rim, alpha);
  ctx.restore();
}

function fog(p: Painter, look: World, lay: Layout, front: boolean): void {
  if (!front) {
    for (const [fx, dy] of [
      [0.12, -0.01],
      [0.5, 0.015],
      [0.88, 0],
    ] as const) fogBank(p, look, p.w * fx, lay.horizon + p.h * dy, p.w * 0.32, p.unit * 0.07, 0.06);
    if (p.level >= 2) {
      fogBank(p, look, p.w * 0.1, lay.horizon - p.h * 0.12, p.w * 0.22, p.unit * 0.06, 0.05);
      fogBank(p, look, p.w * 0.92, lay.horizon - p.h * 0.08, p.w * 0.22, p.unit * 0.06, 0.05);
    }
    return;
  }
  fogBank(p, look, p.w * 0.2, p.h * 0.9, p.w * 0.3, p.unit * 0.05, 0.05);
  fogBank(p, look, p.w * 0.78, p.h * 0.94, p.w * 0.3, p.unit * 0.05, 0.05);
  if (p.level >= 2) fogBank(p, look, p.w * 0.5, p.h * 0.97, p.w * 0.4, p.unit * 0.05, 0.045);
}

function soil(p: Painter, look: World, lay: Layout): void {
  const { ctx } = p;
  const rng = p.rng.fork('soil');
  fillRidge(p, lay.ground, lerpColor(look.palette.bg, look.palette.ink, 0.45), lerpColor(FAR, look.palette.path, 0.18));
  const step = lay.scale * 0.55;
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(VINE, 0.6);
  ctx.lineWidth = Math.max(1, lay.scale * 0.04);
  for (let x = rng.range(0, step); x < p.w; x += step * rng.range(0.7, 1.5)) {
    const y = lay.ground(x);
    if (calmAt(p, x, y, 1.05)) continue;
    const len = lay.scale * rng.range(0.18, 0.32);
    ctx.beginPath();
    for (const lean of [-0.5, 0, 0.45]) {
      ctx.moveTo(x, y + 1);
      ctx.lineTo(x + lean * len, y - len * (1 - Math.abs(lean) * 0.4));
    }
    ctx.stroke();
  }
}

function scarecrow(p: Painter, look: World, lay: Layout): void {
  const { ctx } = p;
  const { ink, spot, rim, path } = look.palette;
  const s = lay.scale;
  const x = lay.portrait ? p.w - Math.max(2.2 * s, p.w * 0.12) : Math.max(2.2 * s, p.w * 0.1);
  const foot = lay.ground(x) + s * 0.5;
  const head = foot - s * 5;
  const arm = head + s * 1.25;
  const line = Math.max(1, s * 0.05);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = rgba(WOOD);
  ctx.lineWidth = s * 0.2;
  ctx.beginPath();
  ctx.moveTo(x, head);
  ctx.lineTo(x, foot);
  ctx.stroke();
  ctx.lineWidth = s * 0.16;
  ctx.beginPath();
  ctx.moveTo(x - s * 1.65, arm);
  ctx.lineTo(x + s * 1.65, arm + s * 0.06);
  ctx.stroke();
  ctx.strokeStyle = rgba(STRAW, 0.8);
  ctx.lineWidth = line;
  ctx.beginPath();
  for (const dir of [-1, 1]) {
    for (let k = 0; k < 4; k++) {
      const a = (k - 1.5) * 0.35;
      ctx.moveTo(x + dir * s * 1.6, arm + s * 0.04);
      ctx.lineTo(x + dir * s * (1.6 + 0.32 * Math.cos(a)), arm + s * (0.04 + 0.32 * Math.sin(a)));
    }
  }
  for (let k = 0; k < 6; k++) {
    const hx = x + (k - 2.5) * s * 0.26;
    ctx.moveTo(hx, head + s * 2.95);
    ctx.lineTo(hx + (k - 2.5) * s * 0.05, head + s * 3.35);
  }
  ctx.stroke();
  ctx.beginPath();
  const shirt = [
    -0.62, 0.75, 0.62, 0.75, 1.6, 1.03, 1.62, 1.57, 0.72, 1.61, 0.78, 3.1, 0.45, 2.95, 0.15, 3.12, -0.2, 2.94, -0.5, 3.1, -0.78,
    2.98, -0.72, 1.61, -1.62, 1.57, -1.6, 1.03,
  ];
  poly(ctx, shirt, x, head, s, s);
  ctx.fillStyle = rgba(SHIRT);
  ctx.fill();
  ctx.strokeStyle = rgba(ink, 0.55);
  ctx.lineWidth = line;
  ctx.stroke();
  ctx.fillStyle = rgba(PATCH);
  ctx.fillRect(x + s * 0.12, head + s * 1.95, s * 0.42, s * 0.38);
  ctx.strokeStyle = rgba(lerpColor(PATCH, rim, 0.3), 0.45);
  ctx.beginPath();
  for (let k = 0; k < 4; k++) {
    const tx = x + s * (0.17 + k * 0.11);
    ctx.moveTo(tx, head + s * 1.9);
    ctx.lineTo(tx, head + s * 2.0);
  }
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, head, s * 0.6, 0, TAU);
  ctx.fillStyle = rgba(SACK);
  ctx.fill();
  ctx.strokeStyle = rgba(ink, 0.55);
  ctx.stroke();
  for (const dir of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(x + dir * s * 0.33, head + s * 0.12, s * 0.1, 0, TAU);
    ctx.fillStyle = rgba(lerpColor(SACK, PUMP_LIGHT, 0.5), 0.5);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x + dir * s * 0.2, head - s * 0.08, s * 0.075, 0, TAU);
    ctx.fillStyle = rgba(ink, 0.85);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(x, head + s * 0.02, s * 0.3, Math.PI * 0.2, Math.PI * 0.8);
  for (const a of [0.35, 0.5, 0.65]) {
    const ax = x + Math.cos(Math.PI * a) * s * 0.3;
    const ay = head + s * 0.02 + Math.sin(Math.PI * a) * s * 0.3;
    ctx.moveTo(ax, ay - s * 0.07);
    ctx.lineTo(ax, ay + s * 0.07);
  }
  ctx.strokeStyle = rgba(ink, 0.75);
  ctx.lineWidth = line * 1.1;
  ctx.stroke();
  const hatEdge = rgba(lerpColor(FAR, path, 0.3), 0.45);
  ctx.beginPath();
  poly(ctx, [-0.5, -0.52, -0.3, -1.15, 0.12, -1.32, 0.5, -1.5, 0.28, -1.05, 0.5, -0.52], x, head, s, s);
  ctx.fillStyle = rgba(HAT);
  ctx.fill();
  ctx.strokeStyle = hatEdge;
  ctx.lineWidth = line;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x - s * 0.47, head - s * 0.68);
  ctx.lineTo(x + s * 0.47, head - s * 0.66);
  ctx.strokeStyle = rgba(lerpColor(HAT, spot, 0.35), 0.8);
  ctx.lineWidth = s * 0.1;
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(x, head - s * 0.47, s * 0.95, s * 0.17, -0.08, 0, TAU);
  ctx.fillStyle = rgba(HAT);
  ctx.fill();
  ctx.strokeStyle = hatEdge;
  ctx.lineWidth = line;
  ctx.stroke();
}

function patchOf(p: Painter, lay: Layout): Gourd[] {
  const rng = p.rng.fork('patch');
  const base = lay.portrait ? Math.min(p.w, p.h) * 0.042 : p.unit * 0.068;
  const out: Gourd[] = [];
  for (let row = 0; row < 2; row++) {
    for (let x = base * rng.range(1.2, 2.2); x < p.w - base * 1.3; ) {
      const r = base * rng.range(0.75, 1.1) * (row === 0 ? 0.78 : 1.06);
      const gy = lay.ground(x);
      const y = Math.min(row === 0 ? gy + r * 0.55 : gy + (p.h - gy) * rng.range(0.38, 0.55), p.h - r * 1.15);
      const carved = rng.chance(0.55);
      if (!calmAt(p, x, y, 1.12)) out.push({ x, y, r, carved });
      x += r * rng.range(2.5, 3.6);
    }
  }
  return out.sort((a, b) => a.y - b.y);
}

function leaf(ctx: CanvasRenderingContext2D, x: number, y: number, len: number, angle: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(len * 0.5, -len * 0.45, len, 0);
  ctx.quadraticCurveTo(len * 0.5, len * 0.45, 0, 0);
  ctx.fillStyle = rgba(LEAF);
  ctx.fill();
  ctx.strokeStyle = rgba(VINE, 0.8);
  ctx.lineWidth = Math.max(1, len * 0.05);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(len * 0.85, 0);
  ctx.stroke();
  ctx.restore();
}

function vines(p: Painter, patch: readonly Gourd[]): void {
  const { ctx } = p;
  const rng = p.rng.fork('vines');
  const row = [...patch].sort((a, b) => a.x - b.x);
  ctx.lineCap = 'round';
  for (let i = 0; i + 1 < row.length; i++) {
    const a = row[i];
    const b = row[i + 1];
    if (a === undefined || b === undefined) continue;
    const sx = a.x;
    const sy = a.y - a.r * 0.8;
    const ex = b.x;
    const ey = b.y - b.r * 0.8;
    const r = Math.min(a.r, b.r);
    const mx = (sx + ex) / 2;
    const my = Math.max(a.y, b.y) + r * 0.2;
    if (Math.abs(ex - sx) > p.w * 0.3 || calmAt(p, mx, my, 1.05)) continue;
    const qx = 2 * mx - (sx + ex) / 2;
    const qy = 2 * my - (sy + ey) / 2;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.quadraticCurveTo(qx, qy, ex, ey);
    ctx.strokeStyle = rgba(VINE, 0.85);
    ctx.lineWidth = Math.max(1, r * 0.08);
    ctx.stroke();
    ctx.beginPath();
    for (let t = 0; t <= TAU * 1.6; t += 0.3) {
      const rr = r * 0.24 * (1 - t / (TAU * 2));
      const cx = mx + Math.cos(t) * rr;
      const cy = my - r * 0.3 + Math.sin(t) * rr;
      if (t === 0) ctx.moveTo(cx, cy);
      else ctx.lineTo(cx, cy);
    }
    ctx.lineWidth = Math.max(1, r * 0.05);
    ctx.stroke();
    for (const t of [0.3, 0.7]) {
      const lx = (1 - t) * (1 - t) * sx + 2 * (1 - t) * t * qx + t * t * ex;
      const ly = (1 - t) * (1 - t) * sy + 2 * (1 - t) * t * qy + t * t * ey;
      leaf(ctx, lx, ly, r * rng.range(0.55, 0.8), rng.range(-2.6, -0.5));
    }
  }
}

function pumpkin(p: Painter, look: World, gd: Gourd): void {
  const { ctx } = p;
  const { x, y, r } = gd;
  const { ink, held, heldLight, rim } = look.palette;
  ctx.beginPath();
  ctx.ellipse(x, y + r * 0.82, r * 1.25, r * 0.26, 0, 0, TAU);
  ctx.fillStyle = rgba(ink, 0.45);
  ctx.fill();
  const body = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.1, x, y, r * 1.3);
  body.addColorStop(0, rgba(PUMP_LIGHT));
  body.addColorStop(0.6, rgba(PUMP));
  body.addColorStop(1, rgba(PUMP_DARK));
  ctx.strokeStyle = rgba(PUMP_DARK, 0.8);
  ctx.lineWidth = Math.max(1, r * 0.05);
  for (const [dx, wx] of [
    [-0.45, 0.78],
    [0.45, 0.78],
    [0, 0.8],
  ] as const) {
    ctx.beginPath();
    ctx.ellipse(x + dx * r, y, wx * r, r * 0.92, 0, 0, TAU);
    ctx.fillStyle = body;
    ctx.fill();
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.moveTo(x - r * 0.08, y - r * 0.82);
  ctx.quadraticCurveTo(x - r * 0.05, y - r * 1.12, x + r * 0.16, y - r * 1.2);
  ctx.lineTo(x + r * 0.2, y - r * 1.08);
  ctx.quadraticCurveTo(x + r * 0.08, y - r, x + r * 0.1, y - r * 0.82);
  ctx.closePath();
  ctx.fillStyle = rgba(STEM);
  ctx.fill();
  ctx.strokeStyle = rgba(ink, 0.5);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(x - r * 0.35, y - r * 0.45, r * 0.18, r * 0.08, -0.5, 0, TAU);
  ctx.fillStyle = rgba(rim, 0.08);
  ctx.fill();
  if (!gd.carved) return;
  glowDot(ctx, x, y + r * 0.1, r * 1.7, held, 0.13);
  ctx.fillStyle = rgba(lerpColor(held, heldLight, 0.3), 0.55);
  ctx.beginPath();
  for (const dir of [-1, 1]) {
    ctx.moveTo(x + dir * r * 0.46, y - r * 0.06);
    ctx.lineTo(x + dir * r * 0.18, y - r * 0.06);
    ctx.lineTo(x + dir * r * 0.32, y - r * 0.34);
    ctx.closePath();
  }
  ctx.moveTo(x - r * 0.5, y + r * 0.14);
  ctx.quadraticCurveTo(x, y + r * 0.46, x + r * 0.5, y + r * 0.14);
  ctx.quadraticCurveTo(x, y + r * 0.74, x - r * 0.5, y + r * 0.14);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(PUMP);
  ctx.fillRect(x - r * 0.06, y + r * 0.29, r * 0.12, r * 0.08);
}

function fireflies(p: Painter, look: World, lay: Layout): void {
  const { ctx } = p;
  const rng = p.rng.fork('fireflies');
  const want = lay.portrait ? 8 : 11;
  const glow = Math.max(5, p.unit * 0.032);
  const placed: Spot[] = [];
  for (let t = 0; placed.length < want && t < want * 40; t++) {
    const x = rng.range(glow, p.w - glow);
    const y = rng.range(p.h * 0.24, p.h * 0.86);
    if (calmAt(p, x, y, 1.02) || placed.some((o) => Math.hypot(o.x - x, o.y - y) < p.unit * 0.14)) continue;
    placed.push({ x, y });
  }
  for (const [i, f] of placed.entries()) {
    const tone = i % 3 === 2 ? look.palette.incoming.circle : look.palette.held;
    const size = rng.range(0.7, 1.2);
    glowDot(ctx, f.x, f.y, glow * size, tone, 0.32);
    ctx.beginPath();
    ctx.arc(f.x, f.y, Math.max(1.1, p.unit * 0.0055) * size, 0, TAU);
    ctx.fillStyle = rgba(look.palette.heldLight, 0.55);
    ctx.fill();
  }
}

export function paint(p: Painter, look: World): void {
  const lay = layoutOf(p);
  sky(p, look, lay);
  stars(p, look, lay);
  const m = moonOf(p, lay);
  moon(p, look, m);
  if (p.level >= 3) bats(p, look, m);
  const far = hills(p, look, lay);
  const posts = fence(p, look, far, lay);
  if (p.level >= 4) owl(p, look, posts, lay);
  fog(p, look, lay, false);
  soil(p, look, lay);
  scarecrow(p, look, lay);
  const patch = patchOf(p, lay);
  vines(p, patch);
  for (const gd of patch) pumpkin(p, look, gd);
  fog(p, look, lay, true);
  fireflies(p, look, lay);
  calmCenter(p, look.palette.bg, 0.6, 1.15);
  grain(p, 0.05, true);
  vignette(p, look.palette.ink, 0.5);
}
